/**
 * The actor-kind command sets of §3.2, ownership checks and approver eligibility. `system` actors have no command
 * that creates a goal or an item (invariant 4): SubmitChangeset and friends are simply not in their sets.
 */
import { autonomyOf, autonomyRefusal, autonomyStateRefusal } from "./autonomy"
import { isPersonOnlyCard } from "./card-chain"
import { capabilityVerdict } from "./policy"
import { agentOf, isProjectAdmin, leadOf, memberOf, runOf } from "./state"
import type {
  Actor,
  Command,
  CommandRejection,
  Ids,
  ProjectState,
  Proposal,
  ProposalDecision,
  UserId,
} from "./types"

type Tag = Command["_tag"]
type SystemProcess = Extract<Actor, { _tag: "system" }>["process"]

const USER: ReadonlySet<Tag> = new Set<Tag>([
  "SubmitChangeset",
  "UndoChangeset",
  "RevertPlan",
  "OpenProposal",
  "ReviseProposal",
  "WithdrawProposal",
  "DecideProposal",
  "SetHold",
  "UpdateProject",
  "CreatePolicy",
  "RevisePolicy",
  "GrantIntegration",
  "RevokeIntegration",
  "CancelRun",
  "RecordReview",
  "PostComment",
  "EditComment",
  "ResolveThread",
  "SetArtifactState",
  "WriteMemory",
  "RecordKpiSample",
  "RecordKpiSamples",
  "CreateProject",
  "RetryItem",
  "UnblockItem",
  "AcceptImpossible",
  "PauseItem",
  "PostArtifact", // people attach briefs, articles and media too (decision 0003 #1)
  // A person's own CLI writes ordinary working knowledge directly, with its actual author and provenance.
  "ProposeMemory",
  "LendRunner", // wave E (decision 0063): the runner's owner lends it to one item (decide checks the owner)
  "ApplyRunnerGrants", // decision 0138: a runner owner's grants changed (the Workspace DO sends it as the owner)
  // wave C (decision 0071, C3): members (not viewers) set, pause, resume and remove monitors (decide checks who)
  "SetMonitor",
  "RemoveMonitor",
  "SetMonitorPaused",
  "ResumeMonitor",
  "RequestProjectWork",
  "CancelProjectWork",
  "LinkProjectWork",
  "ReplyProjectWork",
  "VerifyProjectWork",
  "SendMessage", // decision 0142
  "MarkMessagesRead",
])

/** What a workspace `viewer` may do in a project: read (not a command) and discuss. */
const VIEWER: ReadonlySet<Tag> = new Set<Tag>(["PostComment", "EditComment", "ResolveThread"])

/** Lead and role agents, inside one of their turn runs. */
const AGENT: ReadonlySet<Tag> = new Set<Tag>([
  "SubmitChangeset",
  "UndoChangeset",
  "RevertPlan",
  "OpenProposal",
  "ReviseProposal",
  "WithdrawProposal",
  "DecideProposal",
  "EscalateProposal", // decision 0102: pass a card addressed to it one level up
  "RequestRun",
  "CancelRun",
  "RecordRunStarted",
  "RecordRunEnded",
  "ReportOutcome",
  "RecordReview",
  "PostComment",
  "Ask",
  "Answer",
  "PostArtifact",
  "SetArtifactState",
  "ProposeMemory",
  "RequestOutwardAction",
  "RecordBetVerdict",
  "RetryItem",
  "UnblockItem",
  "AcceptImpossible",
  "PauseItem",
  "RecordKpiSample", // decision 0045: with evidence; the lead or the KPI's area agent (decide)
  "RecordKpiSamples", // decision 0097: many at once, each checked as one
  "SendBrief", // decision 0098: a brief to the agent's keeper
  "SetMonitor",
  "SetMonitorPaused",
  "RemoveMonitor",
  "ResumeMonitor",
  "RequestProjectWork",
  "CancelProjectWork",
  "LinkProjectWork",
  "ReplyProjectWork",
  "VerifyProjectWork",
  "SendMessage", // decision 0142
])

/** The Mayor reads every project and writes only `OpenProposal{plan_change}`. */
const MAYOR: ReadonlySet<Tag> = new Set<Tag>(["OpenProposal", "ReviseProposal", "WithdrawProposal"])

/** Worker and validator runs. `SubmitChangeset` only as spawn_subtask (checked in decide). */
const WORKER: ReadonlySet<Tag> = new Set<Tag>([
  "ReportOutcome",
  "ReportVerdict",
  "PostArtifact",
  "Ask",
  "ProposeMemory",
  "RequestOutwardAction",
  "SubmitChangeset",
  "RecordKpiSample", // decision 0045: with evidence, for a KPI its item names (decide)
  "RecordKpiSamples", // decision 0097
  "SendMessage", // decision 0142 (0140 #23): workers message their peers directly
])

/** Run lifecycle facts relayed by the hub (and permission prompts relayed by Run DOs). Never graph edits. */
const RUNNER: ReadonlySet<Tag> = new Set<Tag>([
  "RecordRunWaiting",
  "RecordRunRequeued",
  "RecordRunLeased",
  "RecordRunStarted",
  "RecordRunMilestone",
  "RecordRunEnded",
  "RaiseRunTaint",
  "RecordPrState", // decision 0059: the PR watch, relayed by the hub

  "RecordOutwardResult",
  "OpenProposal",
])

const SYSTEM: Readonly<Record<SystemProcess, ReadonlySet<Tag>>> = {
  scheduler: new Set<Tag>([
    "RequestRun",
    "RecordRunWaiting",
    "RecordAskWaiting", // re-walk 0166 §2: a queued message's turn, probed by the hub
    "RecordRunRequeued",
    "RecordRunLeased",
    "RecordRunEnded",
    "NoteOverdue",
    "RecordPrState", // decision 0059: only an expired merge (decide checks)
    "RefreshReviewCards", // decision 0067: after a hydration put back the runs a PR needs
    // wave C (decision 0071, C2): the clock in the Project DO. Each only wakes an agent; none creates work
    "FireMonitor",
    "NoteLooseEnds",
    "NoteSeriesDue",
  ]),
  lease_reaper: new Set<Tag>(["RecordRunEnded", "RecordRunWaiting", "RecordRunRequeued"]),
  watchdog: new Set<Tag>(["EscalateStall", "HoldWake", "NoteRunQuiet"]), // NoteRunQuiet: decision 0135 // HoldWake: the chain card (Wave A §4.0.1)
  expirer: new Set<Tag>(["ExpireProposal"]),
  // wave E (decision 0063, spec §5.4): a leaver's live runs end `member_left` (decide checks the asker left)
  membership: new Set<Tag>(["SyncWorkspace", "CancelRun"]),
  kpi_sync: new Set<Tag>(["RecordKpiSample", "RecordKpiSamples"]),
  gateway: new Set<Tag>([
    "RecordOutwardResult",
    "RaiseRunTaint",
    "ReceiveProjectWork",
    "ReceiveWorkControl",
    "ReceiveProjectReply",
    "RecordWorkDelivery",
    "RecordMessageDelivery", // decision 0142: a lead's chat reply pushed to the person's client
  ]),
  upgrade: new Set<Tag>(["UpgradePolicies", "UpgradePolicyPresets"]), // UpgradePolicyPresets: decision 0134 addendum
  github_app: new Set<Tag>(["RecordPrState"]), // decision 0069: a re-read snapshot only (decide checks)
  // decision 0084: the ensure_pages job posts page documents only (decide's page rules check what it may post)
  pages: new Set<Tag>(["PostArtifact"]),
  // decision 0086: system{people} writes workspace people pages in the WorkspaceDO; it issues no project command
  people: new Set<Tag>(),
}

/** Commands that would create work. No system actor may issue them, whatever its process (invariant 4). */
export const CREATES_WORK: ReadonlySet<Tag> = new Set<Tag>([
  "SubmitChangeset",
  "UndoChangeset",
  "RevertPlan",
  "CreateProject",
])

export const isMayor = (s: ProjectState, actor: Actor): boolean =>
  actor._tag === "agent" && s.mayor?.agentId === actor.agentId

export const commandsFor = (s: ProjectState, actor: Actor): ReadonlySet<Tag> => {
  switch (actor._tag) {
    case "user":
      return USER
    case "agent":
      return isMayor(s, actor) ? MAYOR : AGENT
    case "worker":
      return WORKER
    case "runner":
      return RUNNER
    case "system":
      return SYSTEM[actor.process]
  }
}

/**
 * Wave E (decision 0063, gap 8): `system:membership` takes over the agents a leaver kept, for the workspace owner. Its
 * only graph command is a changeset of `TakeOverAgent`s, which creates no work (decide checks that the keeper left).
 */
export const isMembershipTakeOver = (actor: Actor, command: Command): boolean =>
  actor._tag === "system" &&
  actor.process === "membership" &&
  command._tag === "SubmitChangeset" &&
  command.changes.length > 0 &&
  command.changes.every((c) => c._tag === "TakeOverAgent")

/** Step 5 of the pipeline: may this actor kind issue this command at all? Ownership is checked per handler. */
export const authorize = (s: ProjectState, actor: Actor, command: Command): CommandRejection | null => {
  if (isMembershipTakeOver(actor, command)) return null
  if (actor._tag === "system" && CREATES_WORK.has(command._tag))
    return {
      _tag: "Forbidden",
      reason: "system_cannot_create",
      detail: `system:${actor.process} cannot ${command._tag}`,
    }
  if (!commandsFor(s, actor).has(command._tag))
    return { _tag: "Forbidden", reason: "actor_kind", detail: `${actor._tag} may not issue ${command._tag}` }
  // decision 0129: only the project's members act in it (the Project DO adds a workspace member to a workspace-visible
  // project on their first write); before the first membership sync there are no members and everyone counts
  if (actor._tag === "user" && s.members.length > 0 && memberOf(s, actor.userId) === undefined)
    return {
      _tag: "Forbidden",
      reason: "not_owner",
      detail: "only the project's members act in it: join the project or ask one of its admins to add you",
    }
  if (actor._tag === "user" && memberOf(s, actor.userId)?.role === "viewer" && !VIEWER.has(command._tag))
    return { _tag: "Forbidden", reason: "actor_kind", detail: `a viewer may not issue ${command._tag}` }
  if (actor._tag === "agent" && !isMayor(s, actor)) {
    const a = agentOf(s, actor.agentId)
    if (!a) return { _tag: "Forbidden", reason: "actor_kind", detail: "unknown agent" }
    if (a.status !== "active")
      return { _tag: "Forbidden", reason: "actor_kind", detail: `agent is ${a.status}` }
    // decision 0048: in a turn that changes started, the agent's level limits what it may do
    const level = autonomyOf(s, actor)
    // wave C (decision 0071): `remember` routes by the level itself (decide/memory.ts): Act applies, Ask first is a
    // card, Suggest is refused with its own sentence
    if (level !== null && command._tag !== "ProposeMemory") {
      const why = autonomyRefusal(level, command._tag) ?? autonomyStateRefusal(s, level, command)
      if (why) return { _tag: "Forbidden", reason: "autonomy", detail: why }
    }
  }
  if (actor._tag === "worker") {
    const r = runOf(s, actor.runId)
    if (!r || r.itemId !== actor.itemId)
      return { _tag: "Forbidden", reason: "actor_kind", detail: "worker is not on this item" }
  }
  return null
}

/**
 * Owner of a record, or a project admin (decision 0129: the project's admins and the workspace owners/admins).
 * Steering and steer-like commands use this.
 */
export const mayManage = (s: ProjectState, userId: UserId, ownerId: UserId): boolean =>
  userId === ownerId || isProjectAdmin(s, userId)

/**
 * Does this person manage the project? Decision 0129: a project admin (its creator and the people made admin, and every
 * workspace owner/admin). Project settings, routing, workers, repos, policies, integrations, members, visibility.
 */
export const managesProject = (s: ProjectState, userId: UserId): boolean =>
  s.project !== null && isProjectAdmin(s, userId)

/** The machine facts a permission prompt is judged by: whose machine, Just me (`owner`) or The team (`team`). */
export interface PermissionMachine {
  readonly ownerId: UserId
  readonly serves: "owner" | "team"
}

/**
 * Decision 0126 §3 (0127): the machine a permission prompt came from. The one the Project DO stamped on the card
 * (`body.machine`); else the runner facts passed in; else, for a run on a runner, its principal's machine (Just me);
 * null on the Platform Runner or when unknown (the team's: project managers stand in for its owner).
 */
export const permissionMachineOf = (
  s: ProjectState,
  p: Proposal,
  runners?: (runnerId: Ids["RunnerId"]) => PermissionMachine | undefined,
): PermissionMachine | null => {
  if (p.body._tag !== "permission") return null
  // decision 0138: the Platform Runner is the team's (0126 §4): no owner, its project admins stand in
  if (p.body.machine) return p.body.machine.platform ? null : p.body.machine
  const run = runOf(s, p.body.runId)
  const ex = run?.executor
  if (!run || ex?._tag !== "runner") return null
  return runners?.(ex.runnerId) ?? { ownerId: run.ownerId, serves: "owner" }
}

/**
 * Decision 0126 §3 (0127): why this person may not decide this permission prompt, or null. A prompt on a personal
 * machine (Just me) is decided by the machine's owner only. "Always allow" writes a lasting rule: on a machine only
 * its owner sets one (team machines too), into their own list (decision 0138); on the Platform Runner (no machine) a
 * project manager, into the project's list.
 */
export const permissionRefusal = (
  s: ProjectState,
  userId: UserId,
  p: Proposal,
  remember?: "once" | "run" | "always",
): string | null => {
  if (p.body._tag !== "permission") return null
  const machine = permissionMachineOf(s, p)
  const ownerName = (id: UserId) => memberOf(s, id)?.displayName?.trim() || "its owner"
  if (machine?.serves === "owner" && machine.ownerId !== userId)
    return `this prompt came from ${ownerName(machine.ownerId)}'s own machine: only ${ownerName(machine.ownerId)} decides it`
  if (remember !== "always") return null
  if (machine && machine.ownerId !== userId)
    return `only ${ownerName(machine.ownerId)} sets "always" rules on their machine: allow once or for this run`
  if (!machine && !managesProject(s, userId))
    return 'only the project\'s admins set "always" rules here: allow once or for this run'
  // decision 0138: on a machine, the owner's "always" goes to their own list (never the project's), so no admin is needed
  return null
}

/**
 * Who may decide a proposal (§9.3). People (wave E, decision 0063, answer 5; decision 0126 §7): any project member
 * whose role is not `viewer` decides any card, Merge included; decide records whom it was `for` (`recipientOf`). Except
 * what is someone's own (0127): a permission prompt on a personal machine is its owner's (`permissionRefusal`).
 * The lead: only when it is the approver, never its own, never outward/permission (decision 0134: taint is no gate).
 * On a Mayor plan_change the lead adopts (re-filed under its own policy) or rejects, never approves: approval
 * applies the changes exempt. Memory proposals need the lead's curate_memory capability.
 */
export const mayDecide = (
  s: ProjectState,
  actor: Actor,
  p: Proposal,
  decision?: ProposalDecision["_tag"],
): CommandRejection | null => {
  const deny = (
    reason: "not_owner" | "self_approval" | "actor_kind" | "policy_never",
    detail: string,
  ): CommandRejection => ({
    _tag: "Forbidden",
    reason,
    detail,
  })
  if (actor._tag === "user") {
    // before the first membership sync there are no members: everyone counts (as `ownerLeft` does)
    const member = memberOf(s, actor.userId)
    if (s.members.length > 0 && (member === undefined || member.role === "viewer"))
      return deny("not_owner", "only a project member (not a viewer) decides a card")
    // decision 0126 §3 (0127): a permission prompt on someone's own machine is theirs
    const own = permissionRefusal(s, actor.userId, p)
    if (own) return deny("not_owner", own)
    return null
  }
  if (actor._tag === "agent") {
    if (p.proposedBy._tag === "agent" && p.proposedBy.agentId === actor.agentId)
      return deny("self_approval", "an agent never decides its own proposal")
    if (p.body._tag === "outward_action" || p.body._tag === "permission")
      return deny("actor_kind", "outward actions and permission prompts need a person")
    // decision 0102: a card addressed to this agent (the chain): it decides within its authority (decide checks the
    // changes and effects), never a person-only card, never by editing or adopting
    if (p.approver._tag === "agent") {
      if (p.approver.agentId !== actor.agentId)
        return deny(
          "not_owner",
          `${p.ref} is addressed to ${agentOf(s, p.approver.agentId)?.name ?? "another agent"}, not you`,
        )
      // decision 0117: a memory card about a person stays a person's decision, but the agent holding it may drop it as
      // a duplicate of another memory card or memory (agentDecision checks the note names it)
      if (isPersonOnlyCard(p, s) && !(p.body._tag === "memory" && decision === "reject"))
        return deny(
          "actor_kind",
          `${p.ref} is a person's decision: escalate_card it with your recommendation (what you would choose and why)`,
        )
      if (decision === "approve_edited" || decision === "adopt")
        return deny("actor_kind", "decide the card as it stands: approve, reject, answer or send it back")
      return null
    }
    if (p.approver._tag !== "lead" || p.approver.agentId !== actor.agentId)
      return deny("not_owner", "agent is not the approver")
    const lead = leadOf(s)
    if (!lead || lead.id !== actor.agentId) return deny("not_owner", "only the project lead decides")
    if (p.body._tag === "plan_change" && (decision === "approve" || decision === "approve_edited"))
      return deny(
        "actor_kind",
        "the lead adopts a suggestion (under its own policy) or rejects it; it never approves",
      )
    if (p.body._tag === "memory") {
      const scope = p.body.draft?.scope ?? p.body.fromScope
      const areaId = scope?._tag === "area" ? scope.areaId : null
      if (capabilityVerdict(s, lead, "curate_memory", areaId) !== "allow")
        return deny("policy_never", "the lead's policy does not allow curate_memory")
    }
    return null
  }
  return deny("actor_kind", `${actor._tag} never decides proposals`)
}
